import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSquare } from "../context/SquareContext";
import { useCart } from "../context/CartContext";

const Checkout = () => {
  const { payments, ready, error: squareError, createPayment } = useSquare();
  const { cart, total } = useCart();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Redirect if cart is empty
  useEffect(() => {
    if (!cart || cart.length === 0) {
      navigate("/cart");
    }
  }, [cart, navigate]);

  useEffect(() => {
    let card;
    async function initializeCard() {
      if (!payments || !ready) return;

      try {
        card = await payments.card();
        await card.attach("#card-container");
        
        const styles = {
          "input": {
            "fontSize": "16px",
            "fontFamily": "Helvetica Neue",
            "fontWeight": "400",
            "color": "#373F4A",
          },
          "input::placeholder": {
            "color": "#A3ACB9",
          },
        };
        
        await card.configure({
          style: styles
        });
        
        console.log("Card payment form initialized successfully");
      } catch (e) {
        console.error("Error setting up payment form:", e);
        setError("Failed to set up payment form. Please refresh and try again.");
      }
    }

    initializeCard();

    return () => {
      if (card) {
        card.destroy();
      }
    };
  }, [payments, ready]);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const result = await payments.card().tokenize();
      if (result.status === "OK") {
        const payment = await createPayment(result.token);
        navigate("/payment-success");
      } else {
        setError("Payment failed. Please try again.");
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!cart || cart.length === 0) {
    return <div className="p-6">Your cart is empty</div>;
  }

  if (squareError) {
    return <div className="text-red-500 p-6">Failed to load payment system: {squareError.message}</div>;
  }

  if (!ready) {
    return <div className="p-6">Loading payment form...</div>;
  }

  return (
    <div className="max-w-lg mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Checkout</h2>
      
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Order Summary</h3>
        <div className="border-t border-b py-4">
          {cart?.map((item) => (
            <div key={item.id} className="flex justify-between mb-2">
              <span>{item.name} x {item.quantity}</span>
              <span>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-4 font-bold">
          <span>Total</span>
          <span>${total?.toFixed(2) || "0.00"}</span>
        </div>
      </div>

      <form onSubmit={handlePayment} className="space-y-6">
        <div className="border rounded-lg p-4">
          <div id="card-container" className="min-h-[100px]"></div>
        </div>
        
        {error && (
          <div className="text-red-500 text-sm">{error}</div>
        )}
        
        <button
          type="submit"
          disabled={loading || !cart || cart.length === 0}
          className={`w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors
            ${(loading || !cart || cart.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {loading ? 'Processing...' : 'Pay Now'}
        </button>
      </form>
    </div>
  );
};

export default Checkout;
